// The Effect edge of the regulatory drift screen: the one module that runs its read. Each run
// hands React Query a Promise of the read's Result, so a refusal is data the screen renders and a
// 4xx is never retried as if it were a transient throw; an aborted query interrupts its request.
import { queryOptions } from '@tanstack/react-query';
import { Effect, ManagedRuntime } from 'effect';
import type { Result } from 'effect';
import { FetchHttpClient } from 'effect/http';
import type {
  RegulatoryEcosystem,
  RegulatoryEndpointSnapshot,
} from '@finnest/power-admin-contracts';
import { resolvePowerAdminApiBaseUrl } from '../../runtime/power-admin-api.ts';
import { readDriftSnapshot } from './drift.ts';
import type { DriftRequestFailed } from './drift.ts';

const driftRuntime = ManagedRuntime.make(FetchHttpClient.layer);

/** What the read answered: the snapshot or why it failed. */
export type DriftOutcome = Result.Result<RegulatoryEndpointSnapshot, DriftRequestFailed>;

// The fetch is read per run, as the browser (or a test) has it now; the API base is resolved per
// run too.
const runDriftRead = (ecosystem: RegulatoryEcosystem, signal: AbortSignal): Promise<DriftOutcome> =>
  driftRuntime.runPromise(
    Effect.suspend(() => readDriftSnapshot(resolvePowerAdminApiBaseUrl(), ecosystem)).pipe(
      Effect.result,
      Effect.provideService(FetchHttpClient.Fetch, globalThis.fetch),
    ),
    { signal },
  );

type DriftKey = readonly ['power-admin', 'regulatory-drift', RegulatoryEcosystem];

/** The `ecosystem` snapshot query: read once per screen visit, its refusals not retried. */
export const driftSnapshotQueryOptions = (
  ecosystem: RegulatoryEcosystem,
): ReturnType<typeof queryOptions<DriftOutcome, Error, DriftOutcome, DriftKey>> =>
  queryOptions({
    queryKey: ['power-admin', 'regulatory-drift', ecosystem] as const,
    queryFn: ({ signal }) => runDriftRead(ecosystem, signal),
    retry: false,
  });
